import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { Builder } from '@src/routes/content/components/builder'
import { Icon } from '@src/elements/icon/icon'
import EventBus from '@src/lib/event_bus'
import { NotificationTypes } from '@src/util/constants'
import { track } from '@src/util/functions'
import { WorkoutName } from '@src/routes/content/components/workout-builder/workout-name'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { AssignWorkoutToMember } from '@src/routes/content/components/workout-builder/assign-workout-member'
import { WorkoutTypeSettings } from '@src/routes/content/components/workout-builder/workout-type'
import { SaveWorkout } from '@src/routes/content/components/workout-builder/save-workout'
import { WorkoutExerciseSelector } from '@src/routes/content/components/workout-builder/workout-exercise-selector'
import { WorkoutContent } from '@src/routes/content/components/workout-builder/workout-content'
import { WorkoutComposition } from '@src/routes/content/components/workout-builder/workout-composition'
import WorkoutData, { HolderType } from '@src/data/workout-data'
import Member from '@src/data/member'
import camelcaseKeys from 'camelcase-keys'
import { LoadingScreen } from '@src/components/loading-screen/loading-screen/loading-screen'
import { WorkoutService } from '@src/service/workout'
import { ExerciseService } from '@src/service/exercise'
import { MemberService } from '@src/service/member'
import SessionManager from '@src/lib/session_manager'
import { defaultExerciseFilters } from '@src/state/dafaults'

import './workout-builder.sass'

@Component({
  computed: {
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    workout () {
      return this.$store.state.new_workout
    },
    member () {
      return this.$store.state.workoutMember || this.$store.state.memberProfile || this.$store.state.selectedMember
    }
  }
})
export class WorkoutBuilder extends Vue {

  protected showConfirmPopup: boolean = false
  protected needToConfirmExit: boolean = true
  private workout: WorkoutData
  private member: Member
  private service: WorkoutService = new WorkoutService()
  private loading = true

  constructor () {
    super()
    track('/workout-builder')
  }

  async created () {
    if (this.workout.holderType === HolderType.MEMBER && !this.member) { // edit individual wo from my content
      await this.$store.dispatch('setMemberForActiveWorkout', this.$store.state.trainingMember)
      this.needToConfirmExit = true
    }

    window.addEventListener('beforeunload', this.onBeforeUnload)
    await this.loadAllExercises()
    return this.loadWorkout()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CONTENT')} - ${this.$root.$t('PAGE.CONTENT.WORKOUTS.TAB_NAME')}`
  }

  onBeforeUnload (e) {
    e.stopPropagation()
    e.preventDefault()
    e.returnValue = ''
  }

  async loadWorkout () {
    if (this.$route.params.id) {
      let workoutId = parseInt(this.$route.params.id, 10)
      this.service.getWorkout(workoutId)
        .then(response => this.onRetrieveWorkoutSuccessful(response))
        .catch(async error => {
          console.error('failed to retrieve workout', error)
          await this.$store.dispatch('removeNewWorkout')
          this.loading = false
        })
    } else {
      if (!this.workout) await this.$store.dispatch('removeNewWorkout')
      if (!this.isFurdiClub()) {
        this.$store.dispatch('setContinuousWorkout', true)
        this.$store.dispatch('setCircleWorkout', false)
        this.workout.continuousWorkout = true
        this.workout.circleWorkout = false
      }
      this.workout.stationLocked = false
      this.loading = false
    }
  }

  isFurdiClub () {
    const club = this.$store.state.club
    return !!(club && club.name && club.name.toUpperCase().includes('FURDI'))
  }

  async onRetrieveWorkoutSuccessful (response) {
    let exercises: any[] = response.workout.workout_exercises ? this.service.formatExercisesFromServer(response.workout.workout_exercises) : []
    let workoutToEdit = new WorkoutData(camelcaseKeys(response.workout, { deep: true }))
    await this.$store.dispatch('setSelectedWorkout', { workout: workoutToEdit, exercises: exercises })
    if (SessionManager.Instance.isManager()) {
      if (this.workout.holderType === HolderType.MEMBER && !this.member) { // edit individual wo
        await new MemberService().getMember(this.workout.holderId)
          .then((member) => this.$store.dispatch('setMemberForActiveWorkout', member))
      }
    } else {
      this.$store.dispatch('setMemberForActiveWorkout', this.$store.state.user)
    }

    this.loading = false
  }

  loadAllExercises () {
    if (this.$store.state.availableExercises.length === 0) {
      return new ExerciseService().loadAllExercises().then((exercises) => {
        return this.$store.dispatch('setAvailableExercises', exercises)
      })
    }
  }

  beforeDestroy () {
    this.$store.dispatch('filterExercises', defaultExerciseFilters())
    EventBus.off('resetExerciseConfig')
    EventBus.off('setExerciseConfigRepetitions')
    this.$store.dispatch('setMemberForActiveWorkout', null)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  render (h: CreateElement) {
    return this.loading ? <LoadingScreen/> : <Builder cssClass='workout-builder'>
      <WorkoutName slot='topLeft' />

      <div slot='topMiddle'>
        <AssignWorkoutToMember />
        <WorkoutTypeSettings />
      </div>

      <SaveWorkout slot='topRight' />

      <div onClick={this.closeBuilderAttempt} slot='close'><Icon name='util/close-circled' /></div>

      <div slot='tabletCategory2Title'>{this.$t('PAGE.CONTENT.WORKOUTS.WORKOUTS_FILTER')}</div>

      <WorkoutExerciseSelector slot={'selector'} />

      <WorkoutContent slot='builder' />

      <WorkoutComposition slot={'composition'} showAssistant={true} />

      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.closeBuilder}
                    message={this.$t('PAGE.CONTENT.CLOSE_CONFIRMATION')}
                    type={NotificationTypes.CONFIRM} />
    </Builder>
  }

  closeBuilderAttempt () {
    if (this.needToConfirmExit) {
      this.showConfirmPopup = true
    } else {
      this.closeBuilder(true)
    }
  }

  closeBuilder (close?: boolean) {
    this.showConfirmPopup = false
    if (close === true) {
      if (!SessionManager.Instance.isManager() && this.member) {
        const baseRoute = `/members/${this.member.id}`
        this.$router.push(`${baseRoute}/training`)
      } else {
        this.$router.go(-1)
      }
    }
  }
}
